import React, { useEffect, useMemo, useState } from "react";
import { useDebounce } from "@/hooks/debounce";
import { formatDateTime } from "@/services/api/categoryMaster";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import { useAppDispatch } from "@/store/hooks";

import { TextField, Tooltip } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/rootReducer";
import TableSkeleton from "@/components/Table/TableSkeleton";
import Table from "@/components/Table/Table";
import { exportBulkUploadFile, exportBulkUploadReport, fetchTerritoryReport } from "@/store/slices/territorySlice";

const TerritoryMasterReport = () => {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchText, setSearchText] = useState("");
    const debouncedSearch = useDebounce(searchText);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { territoriesReport, isFetching } = useSelector((state: RootState) => state.territory);

    const actionBtnStyle =
        "text-center text-[#5867dd] cursor-pointer text-[14px] transition-all duration-300 ease-in-out hover:text-[#4a57b0]";

    const queryParams = useMemo(
        () => ({
            loadtype: "Territories",
            from: (page - 1) * rowsPerPage + 1,
            text: debouncedSearch,
            to: page * rowsPerPage,
        }),
        [page, rowsPerPage, debouncedSearch],
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
    };

    const handleFetchTerritoryReport = () => {
        dispatch(fetchTerritoryReport(queryParams));
    };

    const handleExportBulkUploadReport = async (fileId: string) => {
        const response = await dispatch(exportBulkUploadReport(fileId));
        saveAsExcelFile(response.payload, "TerritoryBulkUploadReport");
    };

    const handleExportBulkUploadFile = async (fileId: string) => {
        const response = await dispatch(exportBulkUploadFile(fileId));
        saveAsExcelFile(response.payload, "TerritoryBulkUploadFileReport");
    };

    const columns = [
        {
            name: "FileName",
            selector: (row: any) => (
                <span className="inline-block max-w-full overflow-hidden text-ellipsis">
                    <Tooltip title={row?.FileName}>{row?.FileName}</Tooltip>
                </span>
            ),
            width: "25%",
        },
        { name: "Username", selector: (row: any) => row?.Username, width: "20%" },
        {
            name: "Time",
            selector: (row: any) => formatDateTime(row?.Time),
            width: "20%",
        },
        { name: "Status", selector: (row: any) => row?.Status, width: "15%" },
        {
            name: "Actions",
            selector: (row: any) => (
                <div className="flex items-center gap-[10px]">
                    <Tooltip title="View">
                        <button
                            className={actionBtnStyle}
                            onClick={() => navigate(`/territorymaster/view/${row?.FileId}`)}
                        >
                            ViewReport
                        </button>
                    </Tooltip>
                    <Tooltip title="Download BulkUpload Report">
                        <button
                            className={actionBtnStyle}
                            onClick={() => handleExportBulkUploadReport(row?.FileId)}
                        >
                            <DownloadIcon />
                        </button>
                    </Tooltip>
                    <Tooltip title="Download File">
                        <button
                            className={actionBtnStyle}
                            onClick={() => handleExportBulkUploadFile(row?.FileId)}
                        >
                            File <DownloadIcon />
                        </button>
                    </Tooltip>
                </div>
            ),
            width: "20%",
        },
    ];

    const skeletonColumns = columns.map((col) => {
        return { name: col.name, width: col.width };
    });

    useEffect(() => {
        handleFetchTerritoryReport();
    }, [queryParams]);

    return (
        <section className="bg-white pt-[16px] mx-[16px] shadow-[0_0_13px_#523f690d]">
            <div className="border-b border-[#ebedf2] px-[25px] pl-[15px] min-h-[60px] rounded-t-[4px] flex gap-[5px] justify-between items-center">
                <h5 className="text-[16px] text-[#646c9a] font-medium">
                    Territory Report
                </h5>
                <TextField
                    id="search"
                    label="Search"
                    variant="standard"
                    onChange={handleSearchChange}
                    className="mr-[10px] w-[250px]"
                    sx={{
                        "& .MuiInputLabel-root": {
                            marginTop: "-10px",
                        },
                        "& .MuiInput-root": {
                            height: "16px",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                            marginTop: "0px",
                        },
                    }}
                />
            </div>
            <div className="pt-[5px] px-[10px]">
                {isFetching ? (
                    <TableSkeleton columns={skeletonColumns} />
                ) : (
                    <Table
                        columns={columns}
                        data={territoriesReport?.Items || []}
                        paginationProps={{
                            isPagination: true,
                            totalCount: territoriesReport?.TotalRecords || 0,
                            pageSelected: page,
                            setPageSelected: setPage,
                            rowsPerPageValue: rowsPerPage,
                            setRowsPerPageValue: setRowsPerPage,
                        }}
                        paginationLabel={territoriesReport?.TotalRecords > 0 ? "reports" : "report"}
                    />
                )}
            </div>
        </section>
    );
};

export default TerritoryMasterReport;
